import React, { useState, useEffect, useCallback } from 'react';

import exitApi from '/src/apis/exitApi';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import {
    renderLockedFundingTablesSection,
    renderLockedPortfolioExitsSection,
} from '/src/components/LockedContent';
import NewBadge from '/src/components/Reusable/NewBadge';
import PortfolioExitsTable from './PortfolioExitsTable';
import Pagination from '/src/components/Pagination/Pagination';
import AppMagnittLoader from '/src/components/Reusable/AppMagnittLoader';

const RECORDS_TO_LOAD = 10;
const LIMIT_OPTIONS = [10, 20, 30];

const PortfolioExitsSection = React.forwardRef(({ entityData, entityType, entityId, isTeamMember, user, setState }, ref) => {
    const hasDirectoryAccess = !!(
        user?.is_loggedin &&
        (user?.roles?.includes(MAGNITT_ACCESS_ROLES.directory_access) ||
         user?.roles?.includes(MAGNITT_ACCESS_ROLES.limited_directory_access))
    );
    const showAllRounds = !!(isTeamMember || hasDirectoryAccess);

    const [portfolioExitsCount, setPortfolioExitsCount] = useState(null);
    const [rounds, setRounds] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(RECORDS_TO_LOAD);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch exit count for gating (skip when dir access — they always see everything)
    useEffect(() => {
        if (hasDirectoryAccess) return;
        let isMounted = true;
        exitApi.getPortfolioExitsCount(entityId, entityType)
            .then(res => { if (isMounted) setPortfolioExitsCount(res?.totalRecords ?? 0); })
            .catch(() => { if (isMounted) setPortfolioExitsCount(0); });
        return () => { isMounted = false; };
    }, [entityId, entityType, hasDirectoryAccess]);

    const fetchExits = useCallback((pageNum, pageLimit) => {
        setIsLoading(true);
        exitApi.getPortfolioExits(entityId, entityType, pageLimit, pageNum)
            .then(res => {
                setRounds(res.rounds);
                setTotalRecords(res.totalRecords);
                setIsLoading(false);
            })
            .catch(() => {
                setRounds([]);
                setIsLoading(false);
            });
    }, [entityId, entityType]);

    useEffect(() => {
        if (!user?.is_loggedin || !showAllRounds) return;
        fetchExits(1, limit);
    }, [user?.is_loggedin, showAllRounds]);

    const updateState = useCallback((newState) => {
        if (newState.page !== undefined) setPage(newState.page);
        if (newState.limit !== undefined) setLimit(newState.limit);
    }, []);

    // — Not logged in —
    if (!user?.is_loggedin) {
        if (portfolioExitsCount === null || portfolioExitsCount < 1) return null;
        return renderLockedFundingTablesSection(entityType, entityData?.name, 'portfolioExits', setState, {
            numberFunding: portfolioExitsCount,
        });
    }

    // Wait for team-member resolution before rendering
    if (isTeamMember === '') return null;

    // Logged in, no access: hide until count is known and nonzero
    if (!showAllRounds && (portfolioExitsCount === null || portfolioExitsCount < 1)) return null;

    // Has access but data loaded and empty: nothing to show
    if (showAllRounds && rounds !== null && !isLoading && rounds.length === 0) return null;

    // Count for subtitle: use live totalRecords once loaded, fall back to portfolioExitsCount
    const displayCount = showAllRounds
        ? (totalRecords || portfolioExitsCount || 0)
        : (portfolioExitsCount || 0);

    const exitsCountText = displayCount > 0 && (
        <p className="section-subtitle">
            <span className="section-subtitle__name">{entityData?.name}</span> has made
            <span className="section-subtitle__count"> {displayCount}</span>
            {' '}exit{displayCount !== 1 ? 's' : ''}
        </p>
    );

    return (
        <div ref={ref}>
            <div className="funding-box">
                <div id="section-portfolioexits" className="section-portfolioexits">
                    <div className="profile-section-box fundingrounds">
                        <div className="funding-title">
                            <div className="title">
                                <h2 className="profile-section-heading">
                                    Portfolio Exits <NewBadge className="px-2" />
                                </h2>
                                {exitsCountText}
                            </div>
                        </div>

                        {!showAllRounds ? (
                            renderLockedPortfolioExitsSection()
                        ) : (rounds === null || isLoading) ? (
                            <AppMagnittLoader data={{ height: '300px', background: '#FFF' }} />
                        ) : (
                            <PortfolioExitsTable rounds={rounds} hasDirAccess={hasDirectoryAccess} />
                        )}

                        {showAllRounds && totalRecords > LIMIT_OPTIONS[0] && (
                            <div className="funding-table-pagination" data-table-type="portfolioExits">
                                <Pagination
                                    page={page}
                                    totalRecords={totalRecords}
                                    limit={limit}
                                    limitOptions={LIMIT_OPTIONS}
                                    setParentState={updateState}
                                    fetchData={() => fetchExits(page, limit)}
                                    isLoading={isLoading}
                                    tableName="section-portfolioexits"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

PortfolioExitsSection.displayName = 'PortfolioExitsSection';
export default PortfolioExitsSection;
